import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usersAPI, videosAPI, commentsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import { formatDateShort } from '../../utils/formatDate';

const MyVideos = () => {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [channelFilter, setChannelFilter] = useState('');
  const [myChannels, setMyChannels] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [videoUrlModalOpen, setVideoUrlModalOpen] = useState(false);
  const [pendingVideo, setPendingVideo] = useState(null);
  const [tempVideoUrl, setTempVideoUrl] = useState('');

  useEffect(() => {
    fetchMyChannels();
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, channelFilter]);

  const fetchMyChannels = async () => {
    try {
      const response = await usersAPI.getMyChannels();
      setMyChannels(response.data.data || []);
    } catch (error) {
      // Non bloquant
      console.error('Erreur lors du chargement des chaînes:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channelId = channelFilter;
      const response = await usersAPI.getMyVideos(params);
      setVideos(response.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des vidéos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (video, newStatus) => {
    // Si on change de "pending" à "done", ouvrir le modal pour l'URL
    if (video.status === 'pending' && newStatus === 'done') {
      setPendingVideo(video);
      setTempVideoUrl(video.videoUrl || '');
      setVideoUrlModalOpen(true);
    } else {
      // Sinon, mettre à jour directement
      await updateVideoStatus(video, newStatus);
    }
  };

  const updateVideoStatus = async (video, newStatus, videoUrl = null) => {
    try {
      const updateData = { status: newStatus };
      if (videoUrl !== null && videoUrl !== undefined) {
        updateData.videoUrl = videoUrl || null;
      }
      await videosAPI.updateStatus(video._id || video.id, updateData);
      toast.success('Statut mis à jour');
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleVideoUrlModalSubmit = async () => {
    if (pendingVideo) {
      await updateVideoStatus(
        pendingVideo, 
        'done', 
        tempVideoUrl.trim() || undefined
      );
      setVideoUrlModalOpen(false);
      setPendingVideo(null);
      setTempVideoUrl('');
    }
  };

  const handleVideoUrlModalCancel = () => {
    setVideoUrlModalOpen(false);
    setPendingVideo(null);
    setTempVideoUrl('');
  };

  const fetchComments = async (videoId) => {
    try {
      setLoadingComments(true);
      const response = await commentsAPI.getByVideo(videoId);
      setComments(response.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des commentaires');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Le commentaire ne peut pas être vide');
      return;
    }

    try {
      await commentsAPI.create(selectedVideo._id || selectedVideo.id, newComment.trim());
      toast.success('Commentaire ajouté');
      setNewComment('');
      fetchComments(selectedVideo._id || selectedVideo.id);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;

    try {
      await commentsAPI.delete(commentId);
      toast.success('Commentaire supprimé');
      fetchComments(selectedVideo._id || selectedVideo.id);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    if (video) {
      fetchComments(video._id || video.id);
    }
  };

  if (loading) return <Loading />;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Mes Vidéos</h1>

      <div style={styles.filters}>
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          style={styles.select}
        >
          <option value="">Toutes les chaînes</option>
          {myChannels.map((ch) => (
            <option key={ch._id || ch.id} value={ch._id || ch.id}>
              {ch.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="done">Terminées</option>
        </select>
      </div>

      {videos.length === 0 ? (
        <div style={styles.empty}>
          <p>Aucune vidéo</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {videos.map((video) => (
            <div key={video._id || video.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>{video.title}</h3>
                <span style={{
                  ...styles.badge,
                  background: video.status === 'done' ? '#10b981' : '#f59e0b',
                }}>
                  {video.status}
                </span>
              </div>
              <p style={styles.channel}>
                Chaîne: {video.channelId?.name || '-'}
              </p>
              <p style={styles.description}>{video.description}</p>
              <p style={styles.date}>Date prévue: {formatDateShort(video.plannedDate)}</p>
              
              <div style={styles.actions}>
                <button
                  onClick={() => handleVideoSelect(video)}
                  className="btn btn-outline btn-sm"
                >
                  Voir détails
                </button>
                {video.status === 'pending' ? (
                  <button
                    onClick={() => handleStatusChange(video, 'done')}
                    className="btn btn-primary btn-sm"
                  >
                    Marquer comme terminé
                  </button>
                ) : (
                  <span style={styles.completedBadge}>
                    ✓ Terminée
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal pour l'URL de la vidéo lors du changement de statut */}
      <Modal
        isOpen={videoUrlModalOpen}
        onClose={handleVideoUrlModalCancel}
        title="URL de la vidéo (optionnel)"
      >
        <div style={styles.form}>
          <div style={styles.formGroup}>
            <label>URL de la vidéo YouTube</label>
            <input
              type="url"
              value={tempVideoUrl}
              onChange={(e) => setTempVideoUrl(e.target.value)}
              className="input"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <small style={styles.helpText}>
              Ce champ est optionnel. Vous pouvez l'ajouter plus tard.
            </small>
          </div>
          <div style={styles.formActions}>
            <button 
              type="button" 
              onClick={handleVideoUrlModalCancel} 
              className="btn btn-outline"
            >
              Annuler
            </button>
            <button 
              type="button" 
              onClick={handleVideoUrlModalSubmit} 
              className="btn btn-primary"
            >
              Confirmer
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        title={selectedVideo?.title}
        size="lg"
      >
        {selectedVideo && (
          <div style={styles.details}>
            <div style={styles.detailRow}>
              <strong>Chaîne:</strong> {selectedVideo.channelId?.name || '-'}
            </div>
            <div style={styles.detailRow}>
              <strong>Statut:</strong> {selectedVideo.status}
            </div>
            <div style={styles.detailRow}>
              <strong>Date prévue:</strong> {formatDateShort(selectedVideo.plannedDate)}
            </div>
            <div style={styles.detailRow}>
              <strong>Description:</strong>
              <p>{selectedVideo.description}</p>
            </div>
            {selectedVideo.resource && (
              <div style={styles.detailRow}>
                <strong>Ressource:</strong>
                <div style={styles.resourceContent}>
                  {selectedVideo.resource.split('\n').map((line, idx) => (
                    <p key={idx} style={{ margin: '0.25rem 0' }}>{line}</p>
                  ))}
                </div>
              </div>
            )}
            {selectedVideo.resources && selectedVideo.resources.length > 0 && (
              <div style={styles.detailRow}>
                <strong>Ressources liées:</strong>
                <ul>
                  {selectedVideo.resources.map((resource) => (
                    <li key={resource._id || resource.id}>{resource.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {selectedVideo.videoUrl && (
              <div style={styles.detailRow}>
                <strong>URL de la vidéo:</strong>
                <div style={styles.videoUrlContainer}>
                  <a 
                    href={selectedVideo.videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={styles.videoLink}
                  >
                    {selectedVideo.videoUrl}
                  </a>
                </div>
              </div>
            )}

            {/* Section Commentaires */}
            <div style={styles.commentsSection}>
              <h3 style={styles.commentsTitle}>Notes et Commentaires</h3>
              
              {/* Formulaire d'ajout de commentaire */}
              <div style={styles.commentForm}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Ajouter une note ou un commentaire..."
                  style={styles.commentInput}
                  rows={3}
                />
                <button
                  onClick={handleAddComment}
                  className="btn btn-primary btn-sm"
                  style={styles.commentButton}
                >
                  Ajouter
                </button>
              </div>

              {/* Liste des commentaires */}
              {loadingComments ? (
                <div style={styles.loading}>Chargement...</div>
              ) : comments.length === 0 ? (
                <div style={styles.noComments}>Aucun commentaire</div>
              ) : (
                <div style={styles.commentsList}>
                  {comments.map((comment) => (
                    <div key={comment._id || comment.id} style={styles.commentItem}>
                      <div style={styles.commentHeader}>
                        <div>
                          <strong style={styles.commentAuthor}>
                            {comment.userId?.fullName || 'Utilisateur'}
                          </strong>
                          <span style={styles.commentDate}>
                            {formatDateShort(comment.createdAt)}
                          </span>
                        </div>
                        {isAdmin() && (
                          <button
                            onClick={() => handleDeleteComment(comment._id || comment.id)}
                            style={styles.deleteCommentBtn}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <p style={styles.commentText}>{comment.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: '2rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    marginBottom: '1.5rem',
  },
  select: {
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    background: 'white',
    borderRadius: '1rem',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '0.5rem',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    flex: 1,
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'white',
  },
  channel: {
    color: 'var(--text-light)',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
  },
  description: {
    color: 'var(--text)',
    marginBottom: '0.5rem',
    lineHeight: 1.6,
  },
  date: {
    color: 'var(--text-light)',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  empty: {
    padding: '3rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    background: 'white',
    borderRadius: '0.75rem',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  detailRow: {
    padding: '0.75rem 0',
    borderBottom: '1px solid var(--border)',
  },
  resourceContent: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: 'var(--bg-light)',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    marginTop: '0.5rem',
  },
  videoUrlContainer: {
    marginTop: '0.5rem',
  },
  videoLink: {
    color: 'var(--primary)',
    textDecoration: 'none',
    wordBreak: 'break-all',
  },
  commentsSection: {
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: '2px solid var(--border)',
  },
  commentsTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: 'var(--text)',
  },
  commentForm: {
    marginBottom: '1.5rem',
  },
  commentInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '0.5rem',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  commentButton: {
    alignSelf: 'flex-start',
  },
  commentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  commentItem: {
    background: 'var(--light)',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--border)',
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '0.5rem',
  },
  commentAuthor: {
    fontSize: '0.875rem',
    color: 'var(--text)',
    display: 'block',
    marginBottom: '0.25rem',
  },
  commentDate: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
  },
  commentText: {
    fontSize: '0.875rem',
    color: 'var(--text)',
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  deleteCommentBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.25rem',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
  },
  noComments: {
    textAlign: 'center',
    color: 'var(--text-light)',
    padding: '2rem',
    fontSize: '0.875rem',
  },
  loading: {
    textAlign: 'center',
    color: 'var(--text-light)',
    padding: '1rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  formActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  helpText: {
    fontSize: '0.75rem',
    color: 'var(--text-light)',
    marginTop: '0.25rem',
  },
  completedBadge: {
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    background: '#10b981',
    color: 'white',
    display: 'inline-block',
  },
};

export default MyVideos;





